import axios from 'axios';
import { normalize } from 'normalizr';
import { createSlice } from '@reduxjs/toolkit';
import { mergeShallow, exists, cloneDeep, getObjectKeys } from '../helpers/DataHelpers';

import * as RoleActionTypes from '../constants/RoleActionTypes';
import * as OrganizationActionTypes from '../constants/OrganizationActionTypes';
import * as ChatActionTypes from '../constants/ChatActionTypes';
import * as SecureActionTypes from '../constants/SecureActionTypes';
import * as GroupActionTypes from '../constants/GroupActionTypes';

import { setError, createLock, fetchInboxSections, setModalFormInProgress, setFormInProgress, toggleEmailModal } from './uiReducer';
import * as UserHelpers from '../helpers/UserHelpers';
import * as RoleHelpers from '../helpers/RoleHelpers';
import * as SearchHelpers from '../helpers/SearchHelpers';
import NotificationService from '../services/NotificationService';
import OutboundPostMessageService from '../services/OutboundPostMessageService';
import { user, group, tag, channel, role } from '../actions/NormalizrSchema';
import * as TagReducer from './tagReducer';
import * as GroupReducer from './groupReducer';
import * as ChannelReducer from './channelReducer';
import { getRoles } from './roleReducer';
import StorageService from '../services/StorageService';
import { InboxActionTypes, AuthActionTypes, SavedContentActionTypes, Types, UserPermissionsConstants } from '../constants';
import { USER_RESULT_SIZE } from '../constants/AppConstants';

const { THREAD_VIEW } = UserPermissionsConstants;

// REDUCER

const userSlice = createSlice({
  name: 'USER',
  initialState: {
    appointmentLoading: false,
    connectedPartySearchIds: [],
    connectedPartySearchLoading: false,
    contactSearchIds: [],
    loading: false,
    locationFilter: null,
    memberSearchIds: [],
    memberSearchLoading: false,
    modalPhoneSearchIds: [],
    modalPhoneSearchLoading: false,
    myUserIds: [],
    myUsers: {},
    myUsersRaw: [],
    pageLoading: true,
    pageNo: 0,
    paymentRequests: [],
    phoneSearchLoading: false,
    phoneUserSearchIds: [],
    tagFilter: null,
    typeFilter: null,
    userId: null,
    userIds: [],
    contactList: [],
    userSearchIds: [],
    userSearchLoading: false,
    users: null,
    userSearchSource: null,
    totalUserCount: 0,
    totalActiveMemberCount: 0,
    allMembersIds: [],
    allMembers: {},
    totalContacts: 0,
    contactListLoading: false,
    showGlobalSearchModalWrapper: false,
  },
  reducers: {
    receiveContactCreateFormView: state => ({ ...state, loading: false, pageLoading: false }),
    receiveContactEditFormView: receiveUsersData,
    receiveMembersView: receiveUsersData,
    receiveMyUsersRaw: receiveMyUsersRawData,
    receiveMyUsers: receiveMyUsersData,
    receiveUsers: receiveUsersData,
    receiveCurrentUser: receiveUsersData,
    receiveContactListView: receiveUsersData,
    receiveUser: receiveUserData,
    receiveCreateUser: receiveCreateUserData,
    receiveUpdateUser: receiveUpdateUserData,
    receiveSelectedUser: receiveSelectedUserData,
    removeContactList: removeContactListData,
    receiveUsersStatus: (state, action) => ({
      ...state,
      users: mergeShallow(state.users, action.payload),
    }),
    receiveUsersSearch: (state, action) =>
      ({
        ...state,
        users: mergeShallow(state.users, action.payload.users),
        userSearchIds: [...action.payload.userIds],
        userSearchLoading: false,
        userSearchSource: action.payload.userSearchSource,
        showGlobalSearchModalWrapper: true,
      }),
    receiveMembersSearch: (state, action) =>
      ({
        ...state,
        users: mergeShallow(state.users, action.payload.users),
        memberSearchIds: [...action.payload.userIds],
        memberSearchLoading: false,
      }),
    receiveMembers: (state, action) =>
      ({
        ...state,
        users: mergeShallow(state.users, action.payload.users),
        memberSearchIds: [...new Set([...state.memberSearchIds, ...action.payload.userIds])],
        loading: false,
        pageNo: action.payload.pageNo !== undefined ? action.payload.pageNo : state.pageNo,
      }),
    receiveUsersPhonesSearch: (state, action) =>
      ({
        ...state,
        users: mergeShallow(state.users, action.payload.users),
        phoneUserSearchIds: [...action.payload.userIds],
        phoneSearchLoading: false,
      }),
    receiveUsersModalPhonesSearch: (state, action) =>
      ({
        ...state,
        users: mergeShallow(state.users, action.payload.users),
        modalPhoneSearchIds: [...action.payload.userIds],
        modalPhoneSearchLoading: false,
      }),
    receiveConnectedPartySearch: (state, action) =>
      ({
        ...state,
        users: mergeShallow(state.users, action.payload.users),
        connectedPartySearchIds: [...action.payload.userIds],
        connectedPartySearchLoading: false,
      }),
    requestData: state =>
      ({
        ...state,
        loading: true, // tracks loading of users list
      }),
    requestPageData: state =>
      ({
        ...state,
        pageLoading: true, // tracks loading of full page
      }),
    requestContactListData: state =>
      ({
        ...state,
        contactListLoading: true, // tracks loading of contact list
      }),
    requestMembersSearchData: state =>
      ({
        ...state,
        memberSearchLoading: true,
      }),
    requestConnectedPartySearchData: state =>
      ({
        ...state,
        connectedPartySearchLoading: true,
      }),
    requestSearchData: (state, action) =>
      ({
        ...state,
        userSearchLoading: true,
        userSearchSource: action.payload,
      }),
    setUserSearchError: state =>
      ({
        ...state,
        userSearchLoading: false,
      }),
    requestPhoneSearchData: state =>
      ({
        ...state,
        phoneSearchLoading: true,
      }),
    requestModalPhoneSearchData: state =>
      ({
        ...state,
        modalPhoneSearchLoading: true,
      }),
    setAppointmentLoading: (state, action) => ({ ...state, appointmentLoading: action.payload }),
    setTypeFilter: (state, action) =>
      ({
        ...state,
        typeFilter: action.payload,
      }),
    setTagFilter: (state, action) =>
      ({
        ...state,
        tagFilter: action.payload,
      }),
    setLocationFilter: (state, action) =>
      ({
        ...state,
        locationFilter: action.payload,
      }),
    clearUser: state =>
      ({
        ...state,
        userIds: [],
        // userId: null,
        typeFilter: null,
        pageNo: 0,
      }),
    clearUsers: state =>
      ({
        ...state,
        userIds: [],
        pageNo: 0,
      }),
    clearUserSearchResults: state =>
      ({
        ...state,
        phoneUserSearchIds: [],
        modalPhoneSearchIds: [],
        phoneSearchLoading: false,
        modalPhoneSearchLoading: false,
        userSearchIds: [],
        userSearchLoading: false,
        userSearchSource: null,
        memberSearchIds: [],
        memberSearchLoading: false,
        connectedPartySearchIds: [],
        connectedPartySearchLoading: false,
        showGlobalSearchModalWrapper: false,
      }),
    removeUser: (state, action) => {
      const users = cloneDeep(state.users);
      const { reciprocalConnectedParties } = action.payload;
      if (reciprocalConnectedParties && reciprocalConnectedParties.length) {
        reciprocalConnectedParties.forEach((rcp) => {
          users[rcp.fromUserId].connectedParties = users[rcp.fromUserId].connectedParties.filter(id => id !== rcp.id);
        });
      }
      return ({
        ...state,
        userIds: [...state.userIds.filter(u => u !== action.payload.userId)],
        totalUserCount: state.totalUserCount - 1,
        users,
        loading: false,
      });
    },
    receiveContactListUsers: (state, action) =>
      ({
        ...state,
        users: action.payload.users ? mergeShallow(state.users, action.payload.users) : state.users,
        userIds: action.payload.userIds ? [...new Set([...state.userIds, ...action.payload.userIds])] : state.userIds,
        totalUserCount: action.payload.totalUserCount ? action.payload.totalUserCount : state.totalUserCount,
        loading: false,
      }),
    setActiveUser: (state, action) =>
      ({
        ...state,
        userId: action.payload,
      }),
    receiveUserForms: (state, action) =>
      ({
        ...state,
        users: action.payload.users ? mergeShallow(state.users, action.payload.users) : state.users,
      }),
    receiveContactList: receiveUsersData,
  },
  extraReducers: {
    [GroupActionTypes.receiveGroups]: receiveUsersData,
    [GroupActionTypes.receiveGroupView]: receiveUsersData,

    [ChatActionTypes.receiveChatThread]: receiveUsersData,
    [ChatActionTypes.receiveChatThreadUserView]: receiveUsersData,
    [ChatActionTypes.receiveChatThreadGroupView]: receiveUsersData,
    [ChatActionTypes.receiveChatInbox]: receiveUsersData,
    [ChatActionTypes.receiveCreateChatEvent]: receiveUsersData,
    [ChatActionTypes.receiveWebSocketEvent]: receiveUsersData,

    [InboxActionTypes.receiveInboxThreadView]: receiveUsersData,
    [InboxActionTypes.receiveSecureMessageThread]: receiveUsersData,
    [InboxActionTypes.receiveInboxThread]: receiveUsersData,
    [InboxActionTypes.receiveInboxThreadSearch]: receiveUsersData,
    [InboxActionTypes.receiveInbox]: receiveUsersData,
    [InboxActionTypes.receiveCreateEvent]: receiveUsersData,
    [InboxActionTypes.receiveWebSocketEvent]: receiveUsersData,
    [InboxActionTypes.receiveWebSocketMention]: receiveUsersData,
    [InboxActionTypes.receiveEventMentions]: receiveUsersData,

    [SecureActionTypes.receiveWebSocketEvent]: receiveUsersData,
    [SecureActionTypes.receivePatientThread]: receiveUsersData,
    [SecureActionTypes.receivePatientThreadChannelView]: receiveUsersData,
    [SecureActionTypes.receivePatientThreadAllView]: receiveUsersData,

    [OrganizationActionTypes.receiveOrganization]: receiveUsersData,

    [RoleActionTypes.receiveRole]: receiveUsersData,
    [RoleActionTypes.receiveCustomRoles]: receiveUsersData,
    [RoleActionTypes.receiveSystemRoles]: receiveUsersData,

    [AuthActionTypes.setUser]: (state, action) =>
      ({
        ...state,
        users: {
          ...state.users,
          [action.payload.userId]: action.payload.user[action.payload.userId],
        },
        loading: false,
      }),
    [SavedContentActionTypes.receiveEventsForSavedContent]: (state, action) =>
      ({
        ...state,
        users: action.payload.users ? mergeShallow(state.users, action.payload.users) : state.users,
        userIds: [...new Set([...state.userIds, ...action.payload.userIds])],
      }),
  },
});

export default userSlice.reducer;

// ACTIONS

export const {
  receiveMyUsersRaw,
  receiveMyUsers,
  receiveUsers,
  receiveContactListView,
  receiveContactListUsers,
  receiveUser,
  receiveSelectedUser,
  receiveUsersSearch,
  receiveUsersStatus,
  receiveUsersPhonesSearch,
  receiveUsersModalPhonesSearch,
  receiveMembersSearch,
  receiveMembers,
  requestMembersSearchData,
  receiveConnectedPartySearch,
  requestConnectedPartySearchData,
  requestData,
  requestPageData,
  requestSearchData,
  requestPhoneSearchData,
  requestModalPhoneSearchData,
  setActiveUser,
  setTypeFilter,
  setTagFilter,
  setAppointmentLoading,
  setLocationFilter,
  receiveCreateUser,
  receiveUpdateUser,
  clearUser,
  clearUsers,
  clearUserSearchResults,
  setUserSearchError,
  removeUser,
  receiveMembersView,
  receiveContactCreateFormView,
  receiveContactEditFormView,
  receiveContactList,
  removeContactList,
  requestContactListData,
  receiveCurrentUser,
  receiveUserForms,
} = userSlice.actions;


// REDUCER HELPERS

function receiveMyUsersRawData(state, action) {
  return {
    ...state,
    myUsersRaw: action.payload,
  };
}
function receiveMyUsersData(state, action) {
  return {
    ...state,
    myUsers: {
      ...state.myUsers,
      ...action.payload.myUsers,
    },
    myUserIds: action.payload.myUserIds,
  };
}

function receiveUsersData(state, action) {
  return {
    ...state,
    users: action.payload.users ? mergeShallow(state.users, action.payload.users) : state.users, // merging due to partial/full user object scenario
    userIds: action.payload.userIds ? [...new Set([...state.userIds, ...action.payload.userIds])] : state.userIds,
    totalUserCount: exists(action.payload.totalUserCount) ? action.payload.totalUserCount : state.totalUserCount,
    totalActiveMemberCount: action.payload.totalActiveMemberCount || state.totalActiveMemberCount,
    contactList: action.payload.contactList !== undefined ? [...action.payload.contactList] : state.contactList,
    loading: false,
    pageNo: action.payload.pageNo !== undefined ? action.payload.pageNo : state.pageNo,
    pageLoading: false,
    contactListLoading: false,
  };
}

function receiveSelectedUserData(state, action) {
  return {
    ...state,
    users: {
      ...state.users,
      [action.payload.userId]: action.payload.user[action.payload.userId],
    },
    loading: false,
  };
}

function receiveUserData(state, action) {
  return {
    ...state,
    users: {
      ...state.users,
      [action.payload.userId]: action.payload.user[action.payload.userId],
    },
    userIds: [...new Set([action.payload.userId, ...state.userIds])],
    loading: false,
    pageLoading: false,
  };
}

function receiveUpdateUserData(state, action) {
  return {
    ...state,
    users: {
      ...state.users,
      [action.payload.userId]: action.payload.user[action.payload.userId],
    },
    totalActiveMemberCount: (exists(action.payload.active) && action.payload.active) ? state.totalActiveMemberCount + 1 : state.totalActiveMemberCount,
    loading: false,
  };
}

function receiveCreateUserData(state, action) {
  return {
    ...state,
    users: {
      ...state.users,
      [action.payload.userId]: action.payload.user[action.payload.userId],
    },
    userIds: [...new Set([action.payload.userId, ...state.userIds])],
    loading: false,
  };
}

function removeContactListData(state, action) {
  return {
    ...state,
    contactList: [...state.contactList.filter(contactList => contactList.id !== action.payload.contactListId)],
    loading: false,
  };
}


// THUNKS -- ASYNC ACTION CREATORS

export function fetchContactListView(orgId, userType = Types.TYPE_NON_MEMBERS) {
  return (dispatch) => {
    dispatch(requestPageData());
    const contactListViewRequests = [getUsers(userType), ChannelReducer.getChannels()];

    return axios.all(contactListViewRequests)
      .then(axios.spread((userResponse, channelResponse) => {
        const normalizedUser = normalize(userResponse.data.results, [user]);
        const totalUserCount = userResponse.data.count;
        const normalizedChannel = normalize(channelResponse.data, [channel]);

        dispatch(receiveContactListView(getContactListViewPayload(normalizedUser, normalizedChannel, totalUserCount)));
      }))
      .catch(err => dispatch(handleError(err)));
  };
}

export function updateContactList(contactListId, payload) {
  return (dispatch, getState) => {
    dispatch(setError(null));
    return axios.put(`/contactList/${contactListId}`, payload)
      .then((response) => {
        const contactLists = getState().user.contactList;
        const updatedContactList = contactLists.map((list) => {
          const contactList = cloneDeep(list);
          if (contactList.id === response.data.id) {
            contactList.users = response.data.users;
          }
          return contactList;
        });
        const totalUsers = updatedContactList.reduce((acc, list) => [...acc, ...list.users], []);
        const normalizedUser = normalize(totalUsers, [user]);
        const totalUserCount = totalUsers.length;

        dispatch(receiveContactList(getContactListViewPayload(normalizedUser, null, totalUserCount, updatedContactList)));
        NotificationService('updateContactList', response);
        return response.data;
      })
      .catch((err) => {
        dispatch(handleError(err));
      });
  };
}

export function getUsers(userType) {
  return axios.get(`/users?pageNo=0&pageSize=${USER_RESULT_SIZE}&userTypeId=${userType}&sort=ascending&active=true`)
    .catch(err => console.error(err.response || err)); // eslint-disable-line no-console
}

export function createContactList(payload) {
  return (dispatch, getState) => {
    dispatch(setError(null));
    return axios.post('/contactList', payload)
      .then((response) => {
        const { contactList } = getState().user;
        const updatedContactList = [...contactList, response.data];
        const totalUsers = updatedContactList.reduce((acc, list) => [...acc, ...list.users], []);
        const normalizedUser = normalize(totalUsers, [user]);
        const totalUserCount = totalUsers.length;

        dispatch(receiveContactList(getContactListViewPayload(normalizedUser, null, totalUserCount, updatedContactList)));
        NotificationService('createContactList', response);
        return response.data;
      })
      .catch((err) => {
        dispatch(handleError(err));
      });
  };
}

export function fetchContactCreateFormView() {
  return (dispatch) => {
    dispatch(requestData());

    return axios.all([TagReducer.getTags()])
      .then(axios.spread((tagsResponse) => {
        const normalizedTags = normalize(tagsResponse.data, [tag]);

        dispatch(receiveContactCreateFormView(getContactCreateFormView(normalizedTags)));
      }))
      .catch(err => dispatch(handleError(err)));
  };
}

export function fetchContactEditFormView(contactId, typeId) {
  return (dispatch) => {
    dispatch(requestData());

    return axios.all([TagReducer.getTags(), getUserById(contactId), createLock({ typeId, recordId: contactId })])
      .then(axios.spread((tagsResponse, userResponse, lockResponse) => {
        const normalizedTags = normalize(tagsResponse.data, [tag]);
        const normalizedUser = normalize(userResponse.data, user);
        const recordLock = lockResponse.data;

        dispatch(receiveContactEditFormView(getContactEditFormView(normalizedTags, normalizedUser, recordLock)));
      }))
      .catch(err => dispatch(handleError(err)));
  };
}

export function getUserById(userId) {
  return axios.get(`/users/${userId}`)
    .catch(console.log); // eslint-disable-line no-console
}

export function fetchMemberProfileFormView(userId) {
  return (dispatch) => {
    dispatch(requestData());
    return axios.all([GroupReducer.getGroups(), TagReducer.getTags(), ChannelReducer.getChannels(), getProfileMember(userId), getRoles()])
      .then(axios.spread((groupsResponse, tagsResponse, channelsResponse, userResponse, rolesResponse) => {
        const normalizedGroup = normalize(groupsResponse.data, [group]);
        const normalizedTags = normalize(tagsResponse.data, [tag]);
        const normalizedChannels = normalize(channelsResponse.data, [channel]);
        const normalizedUsers = normalize([userResponse.data], [user]);

        const { systemRoles, customRoles } = RoleHelpers.groupRolesByType(rolesResponse.data);
        const normalizedRoles = {
          normalizedSystemRoles: normalize(systemRoles, [role]),
          normalizedCustomRoles: normalize(customRoles, [role]),
        };
        dispatch(receiveMembersView(getMembersView(normalizedGroup, normalizedTags, normalizedChannels, normalizedUsers, normalizedRoles)));
      }))
      .catch(err => dispatch(handleError(err)));
  };
}

export function fetchNewMemberProfileFormView() {
  return (dispatch) => {
    dispatch(requestData());
    return axios.all([GroupReducer.getGroups(), TagReducer.getTags(), ChannelReducer.getChannels(), getRoles(), getUsers(Types.TYPE_MEMBERS)])
      .then(axios.spread((groupsResponse, tagsResponse, channelsResponse, rolesResponse, userResponse) => {
        const normalizedGroup = normalize(groupsResponse.data, [group]);
        const normalizedTags = normalize(tagsResponse.data, [tag]);
        const normalizedChannels = normalize(channelsResponse.data, [channel]);
        const normalizedUser = normalize(userResponse.data.results, [user]);
        const totalUserCount = userResponse.data.count;

        const { systemRoles, customRoles } = RoleHelpers.groupRolesByType(rolesResponse.data);
        const normalizedRoles = {
          normalizedSystemRoles: normalize(systemRoles, [role]),
          normalizedCustomRoles: normalize(customRoles, [role]),
        };
        dispatch(receiveMembersView(getNewMembersView(normalizedGroup, normalizedTags, normalizedChannels, normalizedRoles, normalizedUser, totalUserCount)));
      }))
      .catch(err => dispatch(handleError(err)));
  };
}

function getProfileMember(id) {
  if (!id) throw new Error('Missing "id" parameter in "getProfileMember".');
  return axios.get(`/users/${id}`).catch(err => console.error(err)); // eslint-disable-line no-console
}

export function createUser(payload, formInProgress = true, isModal) {
  return (dispatch) => {
    if (isModal) {
      dispatch(setModalFormInProgress(formInProgress));
    } else {
      dispatch(setFormInProgress(formInProgress));
    }

    return axios.post('/users', payload)
      .then((response) => {
        const normalized = normalize(response.data, user);

        if (UserHelpers.isMember(response.data)) {
          NotificationService('createMember', response);
        } else {
          NotificationService('createContact', response);
        }
        dispatch(setError(null));
        return dispatch(receiveCreateUser(getUserPayload(normalized)));
      })
      .catch((err) => {
        const { property = null } = (err.response && err.response.data) ? err.response.data : {};
        if (!exists(property)) {
          NotificationService('createUser', err.response);
        }
        dispatch(setError(err.response || err));
      });
  };
}

export function shapeUsersPresence(message) {
  return (dispatch) => {
    dispatch(receiveUsersStatus({
      [message.userId]: {
        onlineStatus: message.status,
      },
    }));
  };
}

export function fetchUsers(pageNo = 0, userType, sort = 'ascending', pageSize = USER_RESULT_SIZE, active = true) {
  const url = userType ?
    `/users?pageNo=${pageNo}&pageSize=${pageSize}&sort=${sort}&active=${active}&userTypeId=${userType}` :
    `/users?pageNo=${pageNo}&pageSize=${pageSize}&sort=${sort}&active=${active}`;

  return (dispatch) => {
    dispatch(requestData());

    return axios.get(url)
      .then((response) => {
        const normalized = normalize(response.data.results, [user]);
        const totalUserCount = response.data.count;

        if (normalized.result) {
          let page = pageNo;

          if (normalized.result.length === 0 || normalized.result.length < pageSize) {
            page = pageNo - 1;
          }

          dispatch(receiveUsers(getUsersPayload(normalized, page || 0, totalUserCount)));
        }
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));
      });
  };
}

export function fetchMembers(pageNo = 0, active = true) {
  const url = `/users?pageNo=${pageNo}&pageSize=50&sort=ascending&active=${active}&userTypeId=${Types.TYPE_MEMBER}`;
  return (dispatch) => {
    if (pageNo === 0) {
      dispatch(requestData());
      dispatch(clearUsers());
    }

    return axios.get(url)
      .then((response) => {
        const normalized = normalize(response.data.results, [user]);
        const totalUserCount = response.data.count;
        const totalActiveMemberCount = active ? response.data.count : undefined;
        if (normalized.result) {
          const page = pageNo;

          dispatch(receiveUsers(getUsersPayload(normalized, page || 0, totalUserCount, totalActiveMemberCount)));
        }
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));
      });
  };
}

export function fetchUser(userId) {
  return (dispatch) => {
    dispatch(requestData());
    return axios.get(`/users/${userId}`)
      .then((response) => {
        const normalized = normalize(response.data, user);

        dispatch(receiveUser(getUserPayload(normalized)));

        dispatch(setError(null));
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));
      });
  };
}

// RHIN-5808: Temporarily revert to old search due to not all needed data existing in results
export function fetchMemberSearch(preload = false, search, scope, excludeCurrentUser = 0, active = true) {
  const encodedSearch = encodeURIComponent(search);

  return (dispatch) => {
    dispatch(requestMembersSearchData());
    let url = scope ?
      `/users/search?q=${encodedSearch}&excludeCurrentUser=${excludeCurrentUser}&scope=${scope}` :
      `/users/search?q=${encodedSearch}&excludeCurrentUser=${excludeCurrentUser}`;

    // Preload all members into memberSearchIds for preloaded search components.
    if (preload) {
      url = `/users?pageNo=0&pageSize=50&sort=ascending&userTypeId=${Types.TYPE_MEMBER}&active=${active}`;
    }

    return axios.get(url)
      .then(({ data }) => {
        const normalized = normalize(data, [user]);

        dispatch(receiveMembersSearch(getUsersPayload(normalized)));
      })
      .catch(err => dispatch(handleError(err)));
  };
}

// RHIN-5808: Temporarily revert to old search due to not all needed data existing in results
// export function fetchMemberSearch(preload = false, search, scope, excludeCurrentUser = 0, active = true) {
//   let url = SearchHelpers.getSearchUrl({ search, scope, excludeCurrentUser });

//   // Preload all members into memberSearchIds for preloaded search components.
//   if (preload) {
//     url = `/users?pageNo=0&pageSize=50&sort=ascending&userTypeId=${Types.TYPE_MEMBER}&active=${active}`;
//   }

//   return (dispatch) => {
//     dispatch(requestMembersSearchData());
//     return axios.get(url, {
//       ...!axios?.defaults?.headers?.common?.Authorization && {
//         headers: { Authorization: 'legacy' }, // both values needed for ApiGateway
//       },
//     })
//       .then(({ data }) => {
//         const normalized = normalize(data, [user]);

//         dispatch(receiveMembersSearch(getUsersPayload(normalized)));
//       })
//       .catch(err => dispatch(handleError(err)));
//   };
// }

export function fetchActiveMembers(pageNo = 0) {
  return (dispatch) => {
    dispatch(requestData());
    const url = `/users?pageNo=${pageNo}&pageSize=50&sort=ascending&userTypeId=${Types.TYPE_MEMBER}&active=true`;
    return axios.get(url)
      .then(({ data }) => {
        const normalized = normalize(data.results, [user]);
        if (normalized.result) {
          let page = pageNo;
          if (normalized.result.length === 0 || normalized.result.length < 50) {
            page = pageNo - 1;
          }
          dispatch(receiveMembers(getUsersPayload(normalized, page || 0)));
        }
      })
      .catch(err => dispatch(handleError(err)));
  };
}

export function fetchConnectedPartySearch(search, scope, excludeCurrentUser = 0, userId, selectedUserIds) {
  const idsToExclude = [];
  // (in the context of adding connected parties) userId represents the user being edited, while selectedUserIds represent the existing connected parties on the coverUser
  if (userId) {
    idsToExclude.push(userId);
    // the if statement below is nested because, if no userId exists, then by Rhinogram law neither will selectedUserIds
    if (selectedUserIds) {
      idsToExclude.push(...selectedUserIds);
    }
  }

  const url = SearchHelpers.getSearchUrl({ search, scope, excludeCurrentUser, idsToExclude });

  return (dispatch) => {
    dispatch(requestConnectedPartySearchData());
    return axios.get(url, {
      ...!axios?.defaults?.headers?.common?.Authorization && {
        headers: { Authorization: 'legacy' }, // both values needed for ApiGateway
      },
    })
      .then((response) => {
        const normalized = normalize(response.data, [user]);

        dispatch(receiveConnectedPartySearch(getUsersPayload(normalized)));
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));
      });
  };
}

let cancelUserSearch;
export const clearUserSearch = () => (dispatch) => {
  if (cancelUserSearch) cancelUserSearch();
  dispatch(clearUserSearchResults());
};

export function fetchUserSearch(payload) {
  const { searchValue, scope, type, source, idsToExclude } = payload;
  const url = SearchHelpers.getSearchUrl({ search: searchValue, scope, type, idsToExclude });

  return (dispatch) => {
    if (cancelUserSearch) cancelUserSearch();
    dispatch(requestSearchData(source));

    return axios.get(url, {
      ...!axios?.defaults?.headers?.common?.Authorization && {
        headers: { Authorization: 'legacy' }, // both values needed for ApiGateway
      },
      cancelToken: new axios.CancelToken(((cancelFunction) => {
        cancelUserSearch = cancelFunction;
      })),
    })
      .then((response) => {
        const normalized = normalize(response.data, [user]);
        dispatch(receiveUsersSearch(getUsersPayload(normalized, null, null, null, source)));
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          cancelUserSearch = null;
          console.error(err.response || err); // eslint-disable-line no-console
          dispatch(setError(err.response || err));
          dispatch(setUserSearchError());
        }
      });
  };
}

export function fetchPhoneSearch(search, scope, type, isModal) {
  const url = SearchHelpers.getSearchUrl({ search, scope, type });

  return (dispatch) => {
    if (isModal) {
      dispatch(requestModalPhoneSearchData());
    } else {
      dispatch(requestPhoneSearchData());
    }
    return axios.get(url, {
      ...!axios?.defaults?.headers?.common?.Authorization && {
        headers: { Authorization: 'legacy' }, // both values needed for ApiGateway
      },
    })
      .then((response) => {
        const normalized = normalize(response.data, [user]);
        if (isModal) {
          dispatch(receiveUsersModalPhonesSearch(getUsersPayload(normalized)));
        } else {
          dispatch(receiveUsersPhonesSearch(getUsersPayload(normalized)));
        }
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));
      });
  };
}

export function mergeUsers(slaveId, masterId) {
  return (dispatch) => {
    dispatch(requestData());
    return axios.get(`/users/mergeUsers/${slaveId}/${masterId}`)
      .then((response) => {
        const normalized = normalize(response.data, user);

        dispatch(receiveUser(getUserPayload(normalized)));

        dispatch(removeUser({ userId: slaveId }));

        NotificationService('mergeUser', response);

        dispatch(setError(null));
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));

        NotificationService('mergeUser', err.response);
      });
  };
}

export function deleteUser(userId) {
  return dispatch =>
    axios.delete(`/users/${userId}`)
      .then((response) => {
        const { reciprocalConnectedParties } = response.data;

        dispatch(removeUser({ userId, reciprocalConnectedParties }));

        const statusCode = {
          status: response.status,
        };

        NotificationService('deleteContact', statusCode);

        dispatch(setError(null));
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));

        NotificationService('deleteContact', err.response);
      });
}

export function sendInviteToUser(userId, payload) {
  return async (dispatch) => {
    try {
      const response = await axios.patch(`/users/${userId}/sendInvite`, payload);
      const normalized = normalize(response.data, user);
      const normalizedUser = getUserPayload(normalized);
      const statusCode = {
        status: response.status,
      };
      dispatch(receiveUpdateUser({ ...normalizedUser }));
      NotificationService('sendInvite', statusCode);
      dispatch(setError(null));
    } catch (err) {
      dispatch(setError(err.response || err));
      NotificationService('sendInvite', err.response);
    }
  };
}

export function updateUser(userId, payload, showNotification = true, currentUserId, userHadThreadView) {
  const isPatientExperience = StorageService.readEntry('patientExperience');
  const orgId = StorageService.readEntry('org');
  return (dispatch) => {
    dispatch(toggleEmailModal(false));
    dispatch(setFormInProgress(true));

    let url = `/users/${userId}`;

    if (isPatientExperience) {
      url = `${url}?patientUserId=${userId}&patientOrgId=${orgId}`;
    }

    return axios.patch(url, payload)
      .then((response) => {
        const normalized = normalize(response.data, user);
        const normalizedUser = getUserPayload(normalized);
        const { active } = payload;

        if (currentUserHasNewThreadViewPermission(currentUserId, userId, userHadThreadView, response)) {
          dispatch(fetchInboxSections())
            .then(() => {
              dispatch(receiveUpdateUser({ ...normalizedUser, active }));
            });
        } else {
          dispatch(receiveUpdateUser({ ...normalizedUser, active }));
        }

        if (showNotification) {
          if (UserHelpers.isMember(response.data)) {
            NotificationService('updateMember', response);
          } else {
            NotificationService('updateContact', response);
          }
        }

        dispatch(setError(null));
        return undefined;
      })
      .catch((err) => {
        const { property = null } = (err.response && err.response.data) ? err.response.data : {};
        if (!exists(property)) {
          NotificationService('updateUser', err.response);
        }
        dispatch(setError(err.response || err));
      });
  };
}


export function updateUserEmail(userId, payload) {
  return (dispatch) => {
    const url = `/users/${userId}`;

    return axios.patch(url, payload)
      .then(() => {
        dispatch(setError(null));
      })
      .catch((err) => {
        dispatch(setError(err.response || err));
      });
  };
}

export function updateUserAppointment(userId, payload) {
  return (dispatch) => {
    dispatch(setAppointmentLoading(true));

    return axios.patch(`/users/${userId}/appointment`, payload)
      .then((response) => {
        const normalized = normalize(response.data, user);
        dispatch(receiveUpdateUser(getUserPayload(normalized)));
        dispatch(setError(null));
        dispatch(setAppointmentLoading(false));
        NotificationService('updateAppointment', response);
      })
      .catch((err) => {
        const { property = null } = (err.response && err.response.data) ? err.response.data : {};
        if (!exists(property)) {
          NotificationService('updateAppointment', err.response);
        }
        dispatch(setError(err.response || err));
        dispatch(setAppointmentLoading(false));
      });
  };
}

export function updateUserPassword(payload) {
  return dispatch =>
    axios.patch('/users/changePassword', payload)
      .then((response) => {
        const normalized = normalize(response.data, user);
        dispatch(receiveUpdateUser(getUserPayload(normalized)));
        NotificationService('updateUserPassword', response);
        notifyNative(response.data.loginEmail, payload.newPass);
        dispatch(setError(null));
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        dispatch(setError(err.response || err));
      });
}

function notifyNative(email, password) {
  OutboundPostMessageService.postMessage({
    type: 'passwordUpdated',
    data: { email, password },
  });
}

export function updateUserPreferences(userId, payload) {
  return dispatch =>
    axios.put(`/users/${userId}/preferences`, payload)
      .then((response) => {
        const normalized = normalize(response.data, user);

        dispatch(receiveUpdateUser(getUserPayload(normalized)));

        NotificationService('updateUserPreferences', response);

        dispatch(setError(null));
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console

        NotificationService('updateUserPreferences', err.response);

        dispatch(setError(err.response || err));
      });
}

/*
 * Helper Methods
 */
function handleError(err) {
  return (dispatch) => {
    const error = err.response || err;

    console.error(error); // eslint-disable-line no-console

    dispatch(setError(error));
  };
}

function getContactEditFormView(normalizedTags, normalizedUser, recordLock) {
  return {
    tags: {
      ...normalizedTags.entities.tags,
    },
    tagIds: normalizedTags.result,
    connectedParties: {
      ...normalizedUser.entities.connectedParties,
    },
    connectedPartyIds: getObjectKeys(normalizedUser.entities.connectedParties),
    emails: {
      ...normalizedUser.entities.emails,
    },
    emailIds: getObjectKeys(normalizedUser.entities.emails),
    rhinograms: {
      ...normalizedUser.entities.rhinograms,
    },
    rhinogramIds: getObjectKeys(normalizedUser.entities.rhinograms),
    phones: {
      ...normalizedUser.entities.phones,
    },
    phoneIds: getObjectKeys(normalizedUser.entities.phones),
    users: {
      ...normalizedUser.entities.users,
    },
    userIds: [normalizedUser.result].flat(),
    recordLock,
  };
}

function getContactCreateFormView(normalizedTags) {
  return {
    tags: {
      ...normalizedTags.entities.tags,
    },
    tagIds: getObjectKeys(normalizedTags.entities.tags),
  };
}

function getMembersView(normalizedGroups, normalizedTags, normalizedChannels, normalizedUsers, normalizedRoles) {
  return {
    users: {
      ...normalizedUsers.entities.users,
    },
    userIds: normalizedUsers.result,
    phones: {
      ...normalizedChannels.entities.phones,
    },
    phoneIds: getObjectKeys(normalizedChannels.entities.phones),
    groups: {
      ...normalizedGroups.entities.groups,
    },
    groupIds: getObjectKeys(normalizedGroups.entities.groups),
    channels: {
      ...normalizedChannels.entities.channels,
    },
    channelIds: getObjectKeys(normalizedChannels.entities.channels),
    tags: {
      ...normalizedTags.entities.tags,
    },
    tagIds: getObjectKeys(normalizedTags.entities.tags),
    organizations: {
      ...normalizedUsers.entities.organizations,
    },
    organizationIds: getObjectKeys(normalizedUsers.entities.organizations),
    systemRoles: {
      ...normalizedRoles.normalizedSystemRoles.entities.roles,
    },
    customRoles: {
      ...normalizedRoles.normalizedCustomRoles.entities.roles,
    },
    roleIds: getObjectKeys({ ...normalizedRoles.normalizedCustomRoles.entities.roles, ...normalizedRoles.normalizedSystemRoles.entities.roles }),
  };
}

function getNewMembersView(normalizedGroups, normalizedTags, normalizedChannels, normalizedRoles, normalizedUser, totalUserCount) {
  return {
    users: {
      ...normalizedUser.entities.users,
    },
    userIds: normalizedUser.result,
    totalUserCount,
    connectedPartyIds: getObjectKeys(normalizedUser.entities.connectedParties),
    emails: {
      ...normalizedUser.entities.emails,
    },
    emailIds: getObjectKeys(normalizedUser.entities.emails),
    facebooks: {
      ...normalizedUser.entities.facebooks,
    },
    facebookIds: getObjectKeys(normalizedUser.entities.facebooks),
    rhinograms: {
      ...normalizedUser.entities.rhinograms,
    },
    rhinogramIds: getObjectKeys(normalizedUser.entities.rhinograms),
    organizations: {
      ...normalizedUser.entities.organizations,
    },
    organizationIds: getObjectKeys(normalizedUser.entities.organizations),
    phones: {
      ...normalizedChannels.entities.phones,
    },
    phoneIds: getObjectKeys(normalizedChannels.entities.phones),
    groups: {
      ...normalizedGroups.entities.groups,
    },
    groupIds: getObjectKeys(normalizedGroups.entities.groups),
    channels: {
      ...normalizedChannels.entities.channels,
    },
    channelIds: getObjectKeys(normalizedChannels.entities.channels),
    tags: {
      ...normalizedTags.entities.tags,
    },
    tagIds: getObjectKeys(normalizedTags.entities.tags),
    systemRoles: {
      ...normalizedRoles.normalizedSystemRoles.entities.roles,
    },
    customRoles: {
      ...normalizedRoles.normalizedCustomRoles.entities.roles,
    },
    roleIds: getObjectKeys({ ...normalizedRoles.normalizedCustomRoles.entities.roles, ...normalizedRoles.normalizedSystemRoles.entities.roles }),
  };
}

export function getUser(userId, orgId = null, isPatientExperience = false) {
  if (isPatientExperience) {
    return axios.get(`/users/${userId}?patientUserId=${userId}&patientOrgId=${orgId}`);
  }

  return axios.get(`/users/${userId}`);
}

export function getMyUsersPayload(normalizedUser) {
  return {
    myUsers: {
      ...normalizedUser.entities.users,
    },
    myUserIds: normalizedUser.result,
    connectedParties: {
      ...normalizedUser.entities.connectedParties,
    },
    connectedPartyIds: getObjectKeys(normalizedUser.entities.connectedParties),
    emails: {
      ...normalizedUser.entities.emails,
    },
    emailIds: getObjectKeys(normalizedUser.entities.emails),
    phones: {
      ...normalizedUser.entities.phones,
    },
    phoneIds: getObjectKeys(normalizedUser.entities.phones),
    facebooks: {
      ...normalizedUser.entities.facebooks,
    },
    facebookIds: getObjectKeys(normalizedUser.entities.facebooks),
    rhinograms: {
      ...normalizedUser.entities.rhinograms,
    },
    rhinogramIds: getObjectKeys(normalizedUser.entities.rhinograms),
    groups: {
      ...normalizedUser.entities.groups,
    },
    groupIds: getObjectKeys(normalizedUser.entities.groups),
    channels: {
      ...normalizedUser.entities.channels,
    },
    channelIds: getObjectKeys(normalizedUser.entities.channels),
    tags: {
      ...normalizedUser.entities.tags,
    },
    tagIds: getObjectKeys(normalizedUser.entities.tags),
    organizations: {
      ...normalizedUser.entities.organizations,
    },
    organizationIds: getObjectKeys(normalizedUser.entities.organizations),
    roles: {
      ...normalizedUser.entities.roles,
    },
    roleIds: getObjectKeys(normalizedUser.entities.roles),
  };
}

export function getUserPayload(normalizedUser) {
  return {
    user: normalizedUser.entities.users,
    userId: normalizedUser.result,
    connectedParties: {
      ...normalizedUser.entities.connectedParties,
    },
    connectedPartyIds: getObjectKeys(normalizedUser.entities.connectedParties),
    emails: {
      ...normalizedUser.entities.emails,
    },
    emailIds: getObjectKeys(normalizedUser.entities.emails),
    phones: {
      ...normalizedUser.entities.phones,
    },
    phoneIds: getObjectKeys(normalizedUser.entities.phones),
    facebooks: {
      ...normalizedUser.entities.facebooks,
    },
    facebookIds: getObjectKeys(normalizedUser.entities.facebooks),
    rhinograms: {
      ...normalizedUser.entities.rhinograms,
    },
    rhinogramIds: getObjectKeys(normalizedUser.entities.rhinograms),
    groups: {
      ...normalizedUser.entities.groups,
    },
    groupIds: getObjectKeys(normalizedUser.entities.groups),
    channels: {
      ...normalizedUser.entities.channels,
    },
    channelIds: getObjectKeys(normalizedUser.entities.channels),
    tags: {
      ...normalizedUser.entities.tags,
    },
    tagIds: getObjectKeys(normalizedUser.entities.tags),
    organizations: {
      ...normalizedUser.entities.organizations,
    },
    organizationIds: getObjectKeys(normalizedUser.entities.organizations),
    roles: {
      ...normalizedUser.entities.roles,
    },
    roleIds: getObjectKeys(normalizedUser.entities.roles),
  };
}

export function getUsersPayload(normalizedUser, pageNo, totalUserCount, totalActiveMemberCount, userSearchSource) {
  return {
    users: {
      ...normalizedUser.entities.users,
    },
    userSearchSource,
    totalUserCount,
    totalActiveMemberCount,
    userIds: normalizedUser.result,
    connectedParties: {
      ...normalizedUser.entities.connectedParties,
    },
    connectedPartyIds: getObjectKeys(normalizedUser.entities.connectedParties),
    emails: {
      ...normalizedUser.entities.emails,
    },
    emailIds: getObjectKeys(normalizedUser.entities.emails),
    phones: {
      ...normalizedUser.entities.phones,
    },
    phoneIds: getObjectKeys(normalizedUser.entities.phones),
    facebooks: {
      ...normalizedUser.entities.facebooks,
    },
    facebookIds: getObjectKeys(normalizedUser.entities.facebooks),
    rhinograms: {
      ...normalizedUser.entities.rhinograms,
    },
    rhinogramIds: getObjectKeys(normalizedUser.entities.rhinograms),
    groups: {
      ...normalizedUser.entities.groups,
    },
    groupIds: getObjectKeys(normalizedUser.entities.groups),
    channels: {
      ...normalizedUser.entities.channels,
    },
    channelIds: getObjectKeys(normalizedUser.entities.channels),
    tags: {
      ...normalizedUser.entities.tags,
    },
    tagIds: getObjectKeys(normalizedUser.entities.tags),
    organizations: {
      ...normalizedUser.entities.organizations,
    },
    organizationIds: getObjectKeys(normalizedUser.entities.organizations),
    roles: {
      ...normalizedUser.entities.roles,
    },
    roleIds: getObjectKeys(normalizedUser.entities.roles),
    pageNo,
  };
}

export function getContactListViewPayload(normalizedUser, normalizedChannel, totalUserCount, contactList) {
  return {
    users: {
      ...normalizedUser.entities.users,
    },
    totalUserCount,
    userIds: normalizedUser.result,
    connectedParties: {
      ...normalizedUser.entities.connectedParties,
    },
    connectedPartyIds: getObjectKeys(normalizedUser.entities.connectedParties),
    emails: {
      ...normalizedUser.entities.emails,
    },
    emailIds: getObjectKeys(normalizedUser.entities.emails),
    phones: {
      ...normalizedUser.entities.phones,
    },
    phoneIds: getObjectKeys(normalizedUser.entities.phones),
    facebooks: {
      ...normalizedUser.entities.facebooks,
    },
    facebookIds: getObjectKeys(normalizedUser.entities.facebooks),
    rhinograms: {
      ...normalizedUser.entities.rhinograms,
    },
    rhinogramIds: getObjectKeys(normalizedUser.entities.rhinograms),
    groups: {
      ...normalizedUser.entities.groups,
    },
    groupIds: getObjectKeys(normalizedUser.entities.groups),
    channels: normalizedChannel ? {
      ...normalizedUser.entities.channels,
      ...normalizedChannel.entities.channels,
    } : {
      ...normalizedUser.entities.channels,
    },
    channelIds: normalizedChannel ? [
      ...new Set([
        ...getObjectKeys(normalizedUser.entities.channels),
        ...getObjectKeys(normalizedChannel.entities.channels)]),
    ] : [
      ...new Set([...getObjectKeys(normalizedUser.entities.channels)]),
    ],
    tags: {
      ...normalizedUser.entities.tags,
    },
    tagIds: getObjectKeys(normalizedUser.entities.tags),
    organizations: {
      ...normalizedUser.entities.organizations,
    },
    organizationIds: getObjectKeys(normalizedUser.entities.organizations),
    roles: {
      ...normalizedUser.entities.roles,
    },
    roleIds: getObjectKeys(normalizedUser.entities.roles),
    contactList,
  };
}

function currentUserHasNewThreadViewPermission(currentUserId, userId, userHadThreadView, response) {
  // We are covering an edgecase that asks:
  // 1) Is the current user the user that we are editing (updatingCurrentUser)
  // 2) Was the user previously missing the THREAD_VIEW permission (!userHadThreadView)
  // 3) Does the user now have the THREAD_VIEW permission as a result of the update (userNowHasThreadView)
  const updatingCurrentUser = currentUserId === userId;
  const userPermissions = response.data?.permissions || [];
  const permissionNames = userPermissions.map(permission => permission.name);
  const userNowHasThreadView = permissionNames.includes(THREAD_VIEW);
  // If all of these evaluate to true, then we want to call fetchInboxSections(), because that means it was NOT fetched on initial load.
  return updatingCurrentUser && !userHadThreadView && userNowHasThreadView;
}

export function fetchContactList(isModalOpen) {
  return (dispatch) => {
    if (isModalOpen) {
      dispatch(requestContactListData());
    } else {
      dispatch(requestPageData());
    }
    const contactListRequests = [getContactList(), ChannelReducer.getChannels()];
    return axios.all(contactListRequests)
      .then(axios.spread((contactListResponse, channelResponse) => {
        const contactLists = contactListResponse.data;
        const totalUsers = contactLists.reduce((acc, list) => [...acc, ...list.users], []);
        const normalizedUser = normalize(totalUsers, [user]);
        const totalUserCount = totalUsers.length;
        const normalizedChannel = normalize(channelResponse.data, [channel]);

        dispatch(receiveContactList(getContactListViewPayload(normalizedUser, normalizedChannel, totalUserCount, contactLists)));
      }))
      .catch(err => dispatch(handleError(err)));
  };
}

export function getContactList() {
  return axios.get('/contactList')
    .catch(err => console.error(err.response || err)); // eslint-disable-line no-console
}

export function deleteContactList(contactListId) {
  return dispatch =>
    axios.delete(`/contactList/${contactListId}`)
      .then((response) => {
        dispatch(removeContactList({ contactListId }));
        NotificationService('deleteContactList', response);
        dispatch(setError(null));
      })
      .catch((err) => {
        console.error(err.response || err); // eslint-disable-line no-console
        dispatch(setError(err.response || err));
        NotificationService('deleteContactList', err.response);
      });
}
